import { Component, Input, Output, EventEmitter, AfterViewInit } from "@angular/core";
import { BpSelectorFrequencyModel } from "./selector-models/bp-selector-frequency-model";
import { TimeFrequency, TimeRange } from "@models/savings_plan_example";
import moment from "moment";
import { empty, equal } from "@helpers/utils";

@Component({
	selector: "bp-selector-frequency",
	templateUrl: "./bp-selector-frequency.component.html",
	styleUrls: ["./bp-selector-frequency.component.scss"],
	standalone: false
})
export class BpSelectorFrequencyComponent implements AfterViewInit {
	@Output()
	public modelUpdate: EventEmitter<BpSelectorFrequencyModel> = new EventEmitter<BpSelectorFrequencyModel>();

	@Input()
	public previousStoredModel: BpSelectorFrequencyModel;

	@Input()
	public set planFrequency(frequencies: TimeFrequency[]) {
		if (empty(this._planFrequency)) {
			this._planFrequency = frequencies;
		} else if (!equal(this._planFrequency, frequencies)) {
			this._planFrequency = frequencies;
			this.setDefaults();
		}
	}

	@Input()
	public set planInterval(n: number) {
		if (this._planInterval === 0) {
			this._planInterval = n;
		} else if (this._planInterval !== n) {
			this._planInterval = n;
			this.setDefaults();
		}
	}

	public frequencyDropdownModel: FrequencyDropDownModel = new FrequencyDropDownModel(); // This is used to populate the dropdown and handle selections

	private _planFrequency: TimeFrequency[] = [];
	private _planInterval: number = 0;

	public get planFrequency(): TimeFrequency[] {
		return this._planFrequency;
	}

	public ngAfterViewInit(): void {
		this.setDefaults();
		this.emitModel();
	}

	private setDefaults(): void {
		this.frequencyDropdownModel.primaryDropdownList = this.planFrequency.map((item) => {
			const frequencyItem: FrequencyItem = new FrequencyItem();
			frequencyItem.setFields(item);
			return frequencyItem;
		});
		this.frequencyDropdownModel.tertiaryDropdownList = this.getMinutesInterval();
		if (!this.previousStoredModel) {
			this.frequencyDropdownModel.primarySelection = this.frequencyDropdownModel.primaryDropdownList.find(
				(f) => f.is_default
			);
			if (!this.isPrimarySelectionEmpty()) {
				this.frequencyDropdownModel.secondarySelection =
					this.frequencyDropdownModel.primarySelection.range.find((selection) => selection.is_default);
			}
			this.setDefaultMinutesSelection();
		} else {
			// Setting default values based on previous Model if the user goes back from the summary screen
			this.frequencyDropdownModel.primarySelection = this.frequencyDropdownModel.primaryDropdownList.find((f) =>
				equal(f.text, this.previousStoredModel.primary)
			);
			if (!this.frequencyDropdownModel.primarySelection) {
				// In case of provider update the new provider may not support the previously selected frequency
				this.frequencyDropdownModel.primarySelection = this.frequencyDropdownModel.primaryDropdownList.find(
					(f) => f.is_default
				);
				this.changedValue(this.frequencyDropdownModel.primarySelection);
			} else {
				this.frequencyDropdownModel.secondarySelection =
					this.frequencyDropdownModel.primarySelection.range.find(
						(r) => r.number === this.previousStoredModel.secondary
					);
			}
			this.frequencyDropdownModel.tertiarySelection = this.frequencyDropdownModel.tertiaryDropdownList.find((t) =>
				equal(t, this.previousStoredModel.time)
			);
		}
	}

	// This method sets the default selection always to the next full 15 minutes of the current hour
	private setDefaultMinutesSelection(): void {
		let time = "";
		const date = new Date();
		let hours = date.getHours();
		const minutes = Math.floor((date.getMinutes() + 15) / 15) * 15;
		// Always 15 min add and floor
		if (minutes === 60) {
			time = ":00";
			if (++hours === 24) {
				time = "00" + time;
			} else {
				time = this.addZeroHourUtility(hours) + hours + time;
			}
		} else {
			time = this.addZeroHourUtility(hours) + hours + ":" + minutes;
		}

		// Time is now HH:SS

		this.frequencyDropdownModel.tertiarySelection = this.frequencyDropdownModel.tertiaryDropdownList.find((s) =>
			equal(s, time)
		);
	}

	private addZeroHourUtility(h: number): string {
		return h.toString().length <= 1 ? "0" : "";
	}

	// One of the 3 dropdowns changed the value
	public changedValue(e: FrequencyItem = new FrequencyItem()): void {
		if (e.text && this.frequencyDropdownModel.primaryDropdownList.some((item) => item.text === e.text)) {
			this.frequencyDropdownModel.secondarySelection = this.frequencyDropdownModel.primarySelection.range.find(
				(value) => value.is_default
			);
		}
		this.emitModel();
	}

	private emitModel(): void {
		const model: BpSelectorFrequencyModel = new BpSelectorFrequencyModel();
		model.primary = this.frequencyDropdownModel.primarySelection.text;
		model.primaryName = this.frequencyDropdownModel.primarySelection.name;
		model.secondary = this.frequencyDropdownModel.secondarySelection
			? this.frequencyDropdownModel.secondarySelection.number
			: "0";
		model.secondaryNumber = this.frequencyDropdownModel.secondarySelection
			? this.frequencyDropdownModel.secondarySelection.name
			: "";
		model.time = this.frequencyDropdownModel.tertiarySelection;
		this.modelUpdate.emit(model);
	}

	public isPrimarySelectionEmpty(): boolean {
		return (
			!this.frequencyDropdownModel.primarySelection || empty(this.frequencyDropdownModel.primarySelection.range)
		);
	}

	// Time values used in dropdown (eg: 12:30, 13:00):
	private getMinutesInterval(): string[] {
		const intervalMinutes = [];
		const oneDay = moment.duration(1, "d");
		while (oneDay.asMinutes() > 0) {
			const splitInterval = moment.duration(this._planInterval, "m"); // Eg: every 30 minutes
			oneDay.subtract(splitInterval);
			const timeValue = moment.utc(oneDay.asMilliseconds()).format("HH:mm"); // 12:30, 18:00, 22:30 // for AM/PM change to .format("LT")
			intervalMinutes.push(timeValue);
		}
		intervalMinutes.reverse();
		return intervalMinutes;
	}
}

class FrequencyDropDownModel {
	private _primaryDropdownList: FrequencyItem[];
	private _primarySelection: FrequencyItem = new FrequencyItem();
	private _tertiaryDropdownList: string[] = [];
	private _tertiarySelection: string = "";
	private _secondarySelection: TimeRange = new TimeRange();

	public get primaryDropdownList(): FrequencyItem[] {
		return this._primaryDropdownList;
	}

	public set primaryDropdownList(f: FrequencyItem[]) {
		this._primaryDropdownList = f;
	}

	public get primarySelection(): FrequencyItem {
		return this._primarySelection;
	}

	public set primarySelection(i: FrequencyItem) {
		this._primarySelection = i;
	}

	public get secondarySelection(): TimeRange {
		return this._secondarySelection;
	}

	public set secondarySelection(s: TimeRange) {
		this._secondarySelection = s;
	}

	public get tertiarySelection(): string {
		return this._tertiarySelection;
	}

	public set tertiarySelection(i: string) {
		this._tertiarySelection = i;
	}

	public get tertiaryDropdownList(): string[] {
		return this._tertiaryDropdownList;
	}

	public set tertiaryDropdownList(s: string[]) {
		this._tertiaryDropdownList = s;
	}
}

export class FrequencyItem {
	private _text: string;
	private _name: string;
	private _is_default: boolean;
	private _range: TimeRange[] = [];

	public setFields(item: TimeFrequency): void {
		this.text = item.label;
		this.name = item.name;
		this.is_default = item.is_default;
		this.range = item.range;
	}

	public set text(s: string) {
		this._text = s;
	}

	public get range(): TimeRange[] {
		return this._range;
	}

	public set name(s: string) {
		this._name = s;
	}

	public get name(): string {
		return this._name;
	}

	public set range(a: TimeRange[]) {
		this._range = a;
	}

	public get is_default(): boolean {
		return this._is_default;
	}

	public set is_default(b: boolean) {
		this._is_default = b;
	}

	public get text(): string {
		return this._text;
	}
}
